// What we keep, on a page rather than in a dialog.
//
// **The same shape as `RulesPage`, and the same reason**: this is prose a person
// reads once and a translator has to be able to open, so it is MDX in
// `content/privacy/` rather than a wall of JSX. It is also the one document
// here with a legal job, which makes "a writer can edit it without touching a
// component" less of a nicety than it is for the rules.
//
// **Deliberately not the rules page with a section added.** The rules are about
// how belts work and somebody scrolling for a recipe should not have to scroll
// past a retention period — and a privacy notice that can only be reached
// through a game manual is one nobody found before they typed.
//
// The frame and the type are `documentPage.tsx`, shared with the terms: two
// legal documents linked side by side that are set differently read as one of
// them having been taken less seriously.

import { Footer } from "./Footer";
import { DOC_COMPONENTS, DocumentPage } from "./documentPage";
import { MailHost, MailUse } from "./privacyComponents";
import { useEffect, useState, type ComponentType } from "react";
import type { MDXComponents } from "mdx/types";

/** One entry per locale, dynamically imported — see `content/rules/index.ts`. */
const LOAD: Record<string, () => Promise<{ default: ComponentType<{ components?: MDXComponents }> }>> =
  {
    en: () => import("src/client/ai/content/privacy/en.mdx")
  };

/** Facts about the deployment, not prose. See `privacyComponents.tsx`. */
const COMPONENTS: MDXComponents = { ...DOC_COMPONENTS, MailUse, MailHost };

export function PrivacyPage({ onClose }: { onClose(): void }): React.ReactElement {
  const [Doc, setDoc] = useState<ComponentType<{ components?: MDXComponents }> | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    // A chunk load can fail — an offline reader, or a deploy that replaced the
    // file mid-session. Saying so beats an empty page, and it matters more here
    // than on the rules: a blank privacy notice reads as not having one.
    (LOAD.en ?? LOAD.en)()
      .then((module) => live && setDoc(() => module.default))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  return (
    <DocumentPage
      testid="privacy-page"
      heading="What we keep"
      backTestid="privacy-back"
      onClose={onClose}
      failed={failed}
      source="src/client/ai/content/privacy/en.mdx"
    >
      {Doc && <Doc components={COMPONENTS} />}
      <Footer />
    </DocumentPage>
  );
}
